import React, { useState, useEffect, useContext, useLayoutEffect } from "react";
import { GET } from "../../utils/fetchUtil";
import { useDebounce } from "../../common/hooks/useDebounce";
import { isTouch } from "../../utils/screenUtil";
import InputField from "../../common/input/InputField";
import { useQuery } from "../../common/hooks/useQuery";
import { useTranslation } from "react-i18next";
import { Row, Column } from "../../common/columns";
import DashboardItem from "../../common/dashboard/DashboardItem";
import Tabs from "../../common/tabs/Tabs";
import Tab from "../../common/tabs/Tab";
import { InfoBarContext } from "../../common/info";
import { cutShort } from "../../utils/stringUtil";
import ReceivedItem from "./ReceivedItem";
import { useLocation } from "react-router-dom";
import { smoothScroll } from "../../utils/scrollbarUtil";
import Loader from "../../common/loader/Loader";
import { MessageContext } from "../../common/message";
import { useHistory } from "react-router-dom";

function NewReceivedResponsePage({ user }) {
  const location = useLocation();
  const [response, setResponse] = useState([]);
  const [request, setRequest] = useState(undefined);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 250);
  const { t } = useTranslation();
  const [activeJournalId, setActiveJournalId] = useState(null);
  const infoBar = useContext(InfoBarContext);
  const message = useContext(MessageContext);
  const history = useHistory();
  const historyState = history.location.state;
  const linkTab = history.location.state?.tab;

  const [activeTab, setActiveTab] = useState(0);
  const [tabs, setTabs] = useState([]);
  const queryParams = useQuery();
  const { readyToScroll } = useState(false);
  const [elementScrollId, setElementScrollId] = useState();
  const [pageSettings] = useState(user.applicationConfiguration.content.submitRequestPage.menu);

  // To avoid scroll to selected doc on every re-render, we fetch id, then set it in state and use that state as dep.
  // in other useEffect. Now it won't scroll on every re-render (infoBar context change).
  useEffect(() => {
    setElementScrollId(queryParams.get("uid"));
  }, [queryParams]);

  useLayoutEffect(() => {
    setTimeout(() => {
      smoothScroll("#recit" + elementScrollId);
    }, 0);
  }, [elementScrollId, request]);

  useEffect(() => {
    GET(
      "/requests/submissions?status=1" +
        (debouncedQuery.length > 0 ? `&query=${debouncedQuery}` : ""),
      (requests) => {
        setRequest(requests);

        if (location.search) {
          let sortedFilteredResponse = requests.requests
            .filter((req) => req.title.toString().toLowerCase().includes(query))
            .sort(function (dateA, dateB) {
              return new Date(dateB.submissionDate) - new Date(dateA.submissionDate);
            });
          setResponse(sortedFilteredResponse);
        } else {
          let notFilteredSortedResponse = requests.requests.sort(function (dateA, dateB) {
            return new Date(dateB.submissionDate) - new Date(dateA.submissionDate);
          });
          setResponse(notFilteredSortedResponse);
        }

        return () => setRequest([]);
      },
      (error) => {
        message.toggle({
          title: t("requests.reviewDocuments.action.save.error"),
          content: error,
          hideNo: true,
          yesLabel: t("modalMessage.okLabel"),
          onYes: () => {
            message.toggle({});
          },
        });
      }
    );

    return () => setRequest([]);
  }, [debouncedQuery, location.search, message, query, t]);

  useEffect(() => {
    if (location.search && request) {
      let element = document.getElementById("recitsec" + queryParams.get("id"))?.parentElement;
      smoothScroll(element);
    }
  }, [queryParams]);

  useEffect(() => {
    smoothScroll("#recit" + queryParams.get("uid"));
  }, [readyToScroll]);

  useEffect(() => {
    if (linkTab) {
      setActiveTab(linkTab);
    }
  }, [historyState, linkTab]);

  useEffect(() => {
    if (!infoBar.isOpen) {
      setActiveJournalId(null);
    }
  }, [infoBar.isOpen, setActiveJournalId]);

  useEffect(() => {
    let tabovi = [];
    pageSettings.forEach((tab) => {
      let count = 0;
      let subtabovi = [];

      let filteredLabels =
        !location.search && debouncedQuery.length > 0
          ? tab.items.filter((label) => label.label.toString().toLowerCase().includes(query))
          : tab.items;

      filteredLabels.forEach((label) => {
        let subtabCount = 0;
        let requests = [];
        response.forEach((req) => {
          if (label.id == req.id) {
            requests.push(req);
            count++;
            subtabCount++;
          }
        });
        if (subtabCount > 0) {
          subtabovi.push({
            labels: label.label,
            uid: label.id,
            count: subtabCount,
            requests: requests,
          });
        }
      });

      tabovi.push({
        count: count,
        label: tab.label,
        subtabs: subtabovi,
      });
    });
    setTabs(tabovi.filter((tab) => tab.count > 0));
  }, [pageSettings, response, setTabs, debouncedQuery]);

  useEffect(() => {
    if (!infoBar.isOpen) {
      setActiveJournalId(null);
    }
  }, [infoBar.isOpen, setActiveJournalId]);

  if (request) {
    return !location.search ? (
      <div className="ml-2 mr-2">
        <div className="mt-2 mb-5 is-flex is-flex-direction-row-reverse">
          <div className="field control" style={{ width: isTouch() ? "100%" : "25%" }}>
            <InputField
              placeholder={t("requests.received.searchInputFieldPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </div>
        </div>

        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} prefix={"tab"}>
          {tabs.map((tab, index) => (
            <Tab
              key={tab + index}
              label={tab.label + ` (${tab.count})`}
              render={() => {
                return (
                  <div>
                    <div className="subtitle is-5 has-text-weight-bold">
                      {tab.label + ` (${tab.count})`}
                    </div>
                    <Row className="is-multiline">
                      {tab.subtabs.map((item, itemIndex) => (
                        <Column key={`item${itemIndex}`} className="is-2">
                          <DashboardItem
                            title={item.labels}
                            hasBorder={true}
                            label={cutShort(item.labels, 75, "...")}
                            labelCounter={"(" + item.count + ")"}
                            labelAlignment="left"
                            labelSize="5"
                            link={`${location.pathname}?id=${item.uid}&title=${item.labels}`}
                            minHeight={120}
                            className="is-size-3 pr-2 mr-1"
                          />
                        </Column>
                      ))}
                    </Row>
                  </div>
                );
              }}
            />
          ))}
        </Tabs>
      </div>
    ) : (
      <div className="ml-2 mr-2">
        <div className="mt-2 mb-5 is-flex is-flex-direction-row-reverse">
          <div className="field control" style={{ width: isTouch() ? "100%" : "25%" }}>
            <InputField
              placeholder={t("requests.received.searchInputFieldPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </div>
        </div>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} prefix={"t"}>
          {tabs.map((tab, index) => {
            return (
              <Tab
                key={tab + index}
                label={`${tab.label} (${tab.count})`}
                render={() => {
                  return (
                    <>
                      {tabs[index].subtabs.map((label, gindex) => {
                        return (
                          <div key={"g" + gindex} className="subtitle is-5 has-text-weight">
                            {label.labels} ({label.count})
                            {response
                              .filter((item) => item.id == label.uid)
                              .map((item, iindex) => (
                                <div
                                  id={iindex === 0 ? "recitsec" + item.id : ""}
                                  key={"t" + iindex}
                                  className="mb-5"
                                >
                                  <ReceivedItem
                                    key={"recit" + item.id}
                                    item={item}
                                    activeJournalId={activeJournalId}
                                    setActiveJournalId={setActiveJournalId}
                                  />
                                </div>
                              ))}
                          </div>
                        );
                      })}
                    </>
                  );
                }}
              />
            );
          })}
        </Tabs>
      </div>
    );
  } else {
    return <Loader />;
  }
}

export default NewReceivedResponsePage;
